import RestEndpointsEnum from "../models/RestEndpointsEnum";
import SocketService from "./SocketService";
import SystemConst from "./SystemConst";
import IUserModel from "../models/IUserModel";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";

/**
 * Example usage:
 *
 * // Endpoint without params - TypeScript won't require params
 * const healthUrl = buildRestUrl({
 *   endpoint: RestEndpointsEnum.Health
 * });
 * // Result: "/health"
 *
 * // Endpoint with params - TypeScript will require params object
 * const orderUrl = buildRestUrl({
 *   endpoint: RestEndpointsEnum.GetOrder,
 *   params: { orderGuid: '123-456' }
 * });
 * // Result: "/v1/orders/123-456"
 *
 * // With query parameters
 * const catalogUrl = buildRestUrlWithQuery({
 *   endpoint: RestEndpointsEnum.CatalogGetItems,
 *   params: { catalogGuid: 'abc-123' },
 *   query: { page: 1, limit: 20 }
 * });
 * // Result: "/v1/abc-123/catalogItems/?page=1&limit=20"
 *
 * // TypeScript error if params are missing:
 * // const badUrl = buildRestUrl({
 * //   endpoint: RestEndpointsEnum.GetOrder
 * // }); // ❌ Error: Property 'params' is missing
 *
 * // Runtime error if param value is missing:
 * // const badUrl = buildRestUrl({
 * //   endpoint: RestEndpointsEnum.GetOrder,
 * //   params: {}
 * // }); // ❌ Runtime Error: Missing required parameters: orderGuid
 */

type ExtractParams<T extends string> = T extends `${infer _Start}:${infer Param}/${infer Rest}`
    ? Param | ExtractParams<`/${Rest}`>
    : T extends `${infer _Start}:${infer Param}`
      ? Param
      : never;

type RequiredParams<T extends RestEndpointsEnum> =
    ExtractParams<T> extends never ? Record<string, never> : Record<ExtractParams<T>, string | number>;

type BuildRestUrlOptions<T extends RestEndpointsEnum> =
    ExtractParams<T> extends never ? { endpoint: T; params?: never } : { endpoint: T; params: RequiredParams<T> };

export function buildRestUrl<T extends RestEndpointsEnum>(options: BuildRestUrlOptions<T>): string {
    const { endpoint, params } = options;

    let url: string = endpoint;

    const paramMatches = url.match(/:(\w+)/g);

    if (paramMatches) {
        if (!params) {
            throw new Error(`Missing params object for endpoint ${endpoint}. Required params: ${paramMatches.join(", ")}`);
        }

        const missingParams: string[] = [];

        paramMatches.forEach((match) => {
            const paramName = match.substring(1);
            const paramValue = (params as any)[paramName];

            if (paramValue === undefined || paramValue === null) {
                missingParams.push(paramName);
            } else {
                url = url.replace(match, String(paramValue));
            }
        });

        if (missingParams.length > 0) {
            throw new Error(`Missing required parameters for ${endpoint}: ${missingParams.join(", ")}`);
        }
    }

    return `${SocketService.getEndpointUrl()}${url}`;
}

export function buildRestUrlWithQuery<T extends RestEndpointsEnum>(
    options: BuildRestUrlOptions<T> & { query?: Record<string, any> }
): string {
    const baseUrl = buildRestUrl(options as BuildRestUrlOptions<T>);

    if (options.query && Object.keys(options.query).length > 0) {
        const queryParams = new URLSearchParams();

        Object.entries(options.query).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    return baseUrl;
}

/**
 * Options for building authentication headers
 */
interface AuthHeaderOptions {
    userModel: IUserModel;
    supplierModel: ISupplierModel;
    vendorModel?: IVendorModel;
    includeContentType?: boolean; // Default: true
}

/**
 * Builds authentication headers for native fetch API
 *
 * @param options - Object containing userModel, supplierModel, and optional vendorModel
 * @returns HeadersInit object with authentication headers
 *
 * @example
 * const headers = buildAuthHeadersForFetch({
 *   userModel,
 *   supplierModel,
 *   vendorModel
 * });
 *
 * const response = await fetch(url, {
 *   method: "GET",
 *   headers
 * });
 */
export function buildAuthHeadersForFetch(options: AuthHeaderOptions): HeadersInit {
    const { userModel, supplierModel, vendorModel, includeContentType = true } = options;

    const headers: HeadersInit = {
        userGuid: userModel.guid,
        userToken: userModel.token,
        supplierGuid: supplierModel.guid,
    };

    // Add vendorGuid for non-supplier users
    if (!userModel.isSupplierUser && !vendorModel) {
        throw new Error("Vendor model is required for non-supplier users");
    }

    if (vendorModel) {
        headers.vendorGuid = vendorModel.guid;
    }

    if (includeContentType) {
        headers["Content-Type"] = "application/json";
    }

    return headers;
}

/**
 * Builds authentication headers for ky HTTP client
 *
 * @param options - Object containing userModel, supplierModel, and optional vendorModel
 * @returns HeadersInit object with authentication headers
 *
 * @example
 * const headers = buildAuthHeadersForKy({
 *   userModel,
 *   supplierModel,
 *   vendorModel
 * });
 *
 * const response = await ky.post(url, {
 *   json: payload,
 *   headers
 * });
 */
export function buildAuthHeadersForKy(options: AuthHeaderOptions): HeadersInit {
    // Ky uses the same HeadersInit type as fetch
    return buildAuthHeadersForFetch(options);
}

/**
 * Builds authentication headers for axios HTTP client
 *
 * @param options - Object containing userModel, supplierModel, and optional vendorModel
 * @returns Plain object with authentication headers for axios config
 *
 * @example
 * const headers = buildAuthHeadersForAxios({
 *   userModel,
 *   supplierModel,
 *   vendorModel
 * });
 *
 * const response = await axios.post(url, payload, {
 *   headers
 * });
 */
export function buildAuthHeadersForAxios(options: AuthHeaderOptions): Record<string, string> {
    // Axios expects a plain object for headers
    const headers = buildAuthHeadersForFetch(options);
    return headers as Record<string, string>;
}
